import { createPortal } from 'react-dom';
import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import Header from './Header/Header';
import Navigation from './Navigation/Navigation';
import './style.css';
import { AddressSearchContext, WindowSizeContext } from '/src/App';
import { AreaDataContext } from '/src/Views/PropertyExplorer/PropertyExplorer';
import Summary from './Summary/Summary';
import SolvingFor from './Analysis/SolvingFor/SolvingFor';
import CostBenefit from './CostBenefit/CostBenefit';
import Payoff from './Payoff/Payoff';
import CreatePlanFlow from '/src/Components/CreatePlanFlow/CreatePlanFlow';
import Analysis from './Analysis/Analysis';


const ContentWrapper = ({ children, inline = false, handleOutsideClick = null }) => {
    const { deviceType } = useContext(WindowSizeContext);

    if (inline) {
        return children;
    } else {
        const popupRef = useRef(null);

        useEffect(() => {
            const handleClick = (event) => {
                if (popupRef.current && !popupRef.current.contains(event.target)) {
                    handleOutsideClick?.();
                }
            };

            if (handleOutsideClick) {
                document.addEventListener('mousedown', handleClick);
                return () => document.removeEventListener('mousedown', handleClick);
            }
        }, [handleOutsideClick]);

        // Add effect to handle body scroll
        useEffect(() => {
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = 'unset';
            };
        }, []);

        return createPortal(
            <div className="what-if-machine-overlay">
                <div
                    className={`what-if-machine-popup ${deviceType}`}
                    ref={popupRef}
                    onClick={e => e.stopPropagation()}
                >
                    {children}
                </div>
            </div>,
            document.body
        );
    }
};

const WhatIfMachinePlanSummary = ({
    propertyData,
    selectedProjectsList,
    handleSelectedProjectsListUpdate,
    notSelectedProjectsList,
    handleUserAddingAProject,
    handleUserRemovingAProject,
    handleOutsideClick,
    inline = false
}) => {
    const { deviceType } = useContext(WindowSizeContext);
    const { portalData } = useContext(AddressSearchContext);
    const { labor_types, resources, icons, house_categories } = portalData;


    const { userPlans, setUserPlans, handleCreatePlanFromLabComplete } = useContext(AreaDataContext);



    const [projectsList, setProjectsList] = useState([]);
    const [simulatorData, setSimulatorData] = useState(null);
    const [currentView, setCurrentView] = useState('summary');

    const popupRef = useRef(null);

    useEffect(() => {
        const handleClick = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                handleOutsideClick();
            }
        };

        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [handleOutsideClick]);

    useEffect(() => {
        // Map through the selectedProjectsList and check every project for the diyPreference key, if it doesn't exist then set it to 'professional'
        const selectedProjectsListWithDiyPreference = selectedProjectsList.map(project => {
            if (!project.diyPreference) {
                project.diyPreference = 'pro';
            }
            return project;
        });
        setProjectsList(selectedProjectsListWithDiyPreference);
    }, [selectedProjectsList]);


    useEffect(() => {
        if (projectsList.length > 0) {
            const numProjects = projectsList.length;

            const stats = {
                numProjects: numProjects,
                discounts: {
                    discountIds: [],
                    discountNum: 0
                },
                averages: {
                    complexity: 0,
                    costLabor: 0,
                    costMaterials: 0,
                    disruption: 0,
                    totalCost: 0,
                    logoCount: 0
                },
                totals: {
                    complexity: 0,
                    costLabor: 0,
                    costMaterials: 0,
                    disruption: 0,
                    totalCost: 0,
                    logoCount: 0,
                    totalPayoff: 0,
                    roi: 0
                },
                counts: {
                    projectCategory: {},
                    homeCategory: {},
                    diyFriendly: {},
                    climateRisks: {}
                },
                percentages: {
                    totalPayoff: 0
                },
                payback: {
                    tenYearTotals: {
                        capital_freed: 0,
                        opex: 0,
                        property_value_upside: 0,
                        total: 0
                    },
                    byYear: Array(10).fill(0).map(() => ({
                        capital_freed: 0,
                        opex: {
                            insurance: 0,
                            maintenance: 0,
                            taxes: 0,
                            utilities: 0
                        },
                        property_value_upside: 0,
                        total: 0
                    })),
                    breakEvenYear: null
                }
            };

            // Initialize climate risks array for unique objects
            const climateRisksMap = new Map(); // Using Map to track unique risks by risk_name

            // Calculate sums and counts
            projectsList.forEach(project => {

                // Get the discounts array from the project
                const discountsArray = project.project_discounts_company_specific

                // Get the entity_id from each discount object
                const entityIds = discountsArray.map(discount => discount.entity_id);

                // Add the entityIds to the stats.discountIds array
                stats.discounts.discountIds.push(...entityIds);
                stats.discounts.discountNum += entityIds.length;



                // Get the diyPreference from the project
                const diyPreference = project.diyPreference;

                let totalCostToUse = 0;
                let laborCostToUse = 0;
                let totalPayoffToUse = project.project_payoff_by_scenario[0].by_total.total || 0; // Simplified - no DIY adjustment

                // Keep DIY logic for costs
                if (diyPreference === 'pro') {
                    totalCostToUse = project.project_total_cost || 0;
                    laborCostToUse = project.project_cost_labor || 0;
                } else {
                    totalCostToUse = project.project_cost_materials || 0;
                    laborCostToUse = 0;
                }

                // Add to totals
                stats.totals.complexity += project.project_complexity || 0;
                stats.totals.costLabor += laborCostToUse || 0;
                stats.totals.costMaterials += project.project_cost_materials || 0;
                stats.totals.disruption += project.project_disruption || 0;
                stats.totals.totalCost += totalCostToUse || 0;
                stats.totals.logoCount += project.project_discounts_company_logos?.length || 0;
                stats.totals.totalPayoff += totalPayoffToUse;

                // Count categories
                const category = project.project_category;
                const homeCategory = project.project_home_category_id;
                const diyFriendly = project.project_diy_friendly;

                stats.counts.projectCategory[category] = (stats.counts.projectCategory[category] || 0) + 1;
                stats.counts.homeCategory[homeCategory] = (stats.counts.homeCategory[homeCategory] || 0) + 1;
                stats.counts.diyFriendly[diyFriendly] = (stats.counts.diyFriendly[diyFriendly] || 0) + 1;

                // Add payback data
                const payoffData = project.project_payoff_by_scenario[0]; // Using first scenario

                // Get the last year values for capital_freed and property_value_upside
                const lastYearCapitalFreed = payoffData.by_value_type.capital_freed.by_year[9] || 0;
                const lastYearPropertyValue = payoffData.by_value_type.property_value_upside?.by_year[9] || 0;

                // Sum all years for capital_freed and property_value_upside
                const totalCapitalFreed = payoffData.by_value_type.capital_freed.by_year.reduce((sum, val) => sum + (val || 0), 0);
                const totalPropertyValue = payoffData.by_value_type.property_value_upside?.by_year.reduce((sum, val) => sum + (val || 0), 0) || 0;

                // Sum up all opex values across years
                let totalOpex = 0;
                const opexCategories = ['insurance', 'maintenance', 'taxes', 'utilities'];
                opexCategories.forEach(category => {
                    totalOpex += payoffData.by_value_type.opex[category].by_year.reduce((sum, val) => sum + val, 0);
                });

                // Add to ten year totals
                stats.payback.tenYearTotals.capital_freed += totalCapitalFreed;
                stats.payback.tenYearTotals.opex += totalOpex;
                stats.payback.tenYearTotals.property_value_upside += totalPropertyValue;
                stats.payback.tenYearTotals.total =
                    stats.payback.tenYearTotals.capital_freed +
                    stats.payback.tenYearTotals.opex +
                    stats.payback.tenYearTotals.property_value_upside;

                // Process capital freed
                payoffData.by_value_type.capital_freed.by_year.forEach((amount, yearIndex) => {
                    stats.payback.byYear[yearIndex].capital_freed += amount;
                });

                // Process opex categories
                opexCategories.forEach(category => {
                    payoffData.by_value_type.opex[category].by_year.forEach((amount, yearIndex) => {
                        stats.payback.byYear[yearIndex].opex[category] += amount;
                    });
                });

                // Process property value upside
                if (payoffData.by_value_type.property_value_upside) {
                    payoffData.by_value_type.property_value_upside.by_year.forEach((amount, yearIndex) => {
                        stats.payback.byYear[yearIndex].property_value_upside += amount;
                    });
                }

                // Count climate risks and collect unique risk objects
                if (project.project_associated_climate_risks) {
                    project.project_associated_climate_risks.forEach(risk => {
                        // Count occurrences
                        stats.counts.climateRisks[risk.risk_name] = (stats.counts.climateRisks[risk.risk_name] || 0) + 1;

                        // Store unique risk objects
                        if (!climateRisksMap.has(risk.risk_name)) {
                            climateRisksMap.set(risk.risk_name, risk);
                        }
                    });
                }
            });

            // Take the discountIds array and remove duplicates, updating in place
            stats.discounts.discountIds = [...new Set(stats.discounts.discountIds)];

            // Get the list of all projects
            const allProjectsList = propertyData.data.recommended_projects.projects_list;

            // Get the total payoff across all projects
            const allProjectsPayoff = allProjectsList.reduce((acc, project) => acc + project.project_payoff_by_scenario[0].by_total.total, 0);
            stats.percentages.totalPayoff = (stats.totals.totalPayoff / allProjectsPayoff);

            // Get the total cost across all projects
            const allProjectsCost = allProjectsList.reduce((acc, project) => acc + project.project_total_cost, 0);
            stats.percentages.totalCost = (stats.totals.totalCost / allProjectsCost);

            // Calculate averages
            const count = projectsList.length;
            stats.averages.complexity = stats.totals.complexity / count;
            stats.averages.costLabor = stats.totals.costLabor / count;
            stats.averages.costMaterials = stats.totals.costMaterials / count;
            stats.averages.disruption = stats.totals.disruption / count;
            stats.averages.totalCost = stats.totals.totalCost / count;
            stats.averages.logoCount = stats.totals.logoCount / count;

            // Calculate ROI
            stats.totals.roi = (stats.totals.totalPayoff / stats.totals.totalCost);

            // Calculate total payback per year and find breakeven year
            let cumulativePayback = 0;
            const totalCost = stats.totals.totalCost;

            // Initialize totals for each value stream
            let totalsByType = {
                capital_freed: 0,
                opex: 0,
                property_value_upside: 0
            };

            stats.payback.byYear.forEach((yearData, yearIndex) => {
                // Add to totals by type
                totalsByType.capital_freed += yearData.capital_freed;
                totalsByType.opex += Object.values(yearData.opex).reduce((sum, val) => sum + val, 0);
                totalsByType.property_value_upside += yearData.property_value_upside;

                // Existing yearly calculations
                const yearTotal = yearData.capital_freed +
                    Object.values(yearData.opex).reduce((sum, val) => sum + val, 0) +
                    yearData.property_value_upside;

                yearData.total = yearTotal;
                cumulativePayback += yearTotal;

                // Check for breakeven
                if (stats.payback.breakEvenYear === null && cumulativePayback >= totalCost) {
                    stats.payback.breakEvenYear = yearIndex + 1;
                }
            });

            // Calculate grand total of all value streams
            const grandTotal = totalsByType.capital_freed +
                totalsByType.opex +
                totalsByType.property_value_upside;

            // Calculate percentages
            stats.payback.pctByType = {
                capital_freed: totalsByType.capital_freed / grandTotal,
                opex: totalsByType.opex / grandTotal,
                property_value_upside: totalsByType.property_value_upside / grandTotal
            };

            // Convert climate risks Map to array
            const uniqueClimateRisks = Array.from(climateRisksMap.values());

            // Calculate percentages for each project relative to totals
            const projectsBreakdown = projectsList.map(project => {

                // Get the diyPreference from the project
                const diyPreference = project.diyPreference;

                let totalCostToUse = 0;
                let laborCostToUse = 0;

                // Keep DIY logic for costs
                if (diyPreference === 'pro') {
                    totalCostToUse = project.project_total_cost || 0;
                    laborCostToUse = project.project_cost_labor || 0;
                } else {
                    totalCostToUse = project.project_cost_materials || 0;
                    laborCostToUse = 0;
                }


                return {
                    project_id: project.project_id,
                    percentages: {
                        total_cost: totalCostToUse / stats.totals.totalCost,
                        materials_cost: project.project_cost_materials / stats.totals.costMaterials,
                        labor_cost: laborCostToUse / stats.totals.costLabor,
                        total_payoff: project.project_payoff_by_scenario[0].by_total.total / stats.totals.totalPayoff,
                        payoff_capital_freed: project.project_payoff_by_scenario[0].by_value_type.capital_freed.by_year[9] / stats.payback.tenYearTotals.capital_freed,
                        payoff_opex: Object.values(project.project_payoff_by_scenario[0].by_value_type.opex).reduce((sum, category) => sum + category.by_year.reduce((yearSum, val) => yearSum + val, 0), 0) / stats.payback.tenYearTotals.opex,
                        payoff_value_upside: (project.project_payoff_by_scenario[0].by_value_type.property_value_upside?.by_year[9] || 0) / stats.payback.tenYearTotals.property_value_upside
                    }
                };
            });

            // Add projectsBreakdown to stats
            stats.projectsBreakdown = projectsBreakdown;

            setSimulatorData(stats);
            // setSimulatorData(prevData => ({
            //     ...prevData,
            //     counts: stats.counts,
            //     climate: uniqueClimateRisks,
            //     averages: stats.averages,
            //     totals: stats.totals,
            //     payback: stats.payback,
            //     projectsBreakdown: stats.projectsBreakdown // Add to simulator data
            // }));
        }
    }, [projectsList]);


    const sectionMenuItems = [
        {
            label: 'Summary',
            value: 'summary'
        },
        {
            label: "What You're Solving For",
            value: 'solving-for'
        },
        {
            label: 'Payoff',
            value: 'payoff'
        },
        {
            label: 'Costs vs. Benefits',
            value: 'costs-vs-benefits'
        },
        {
            label: 'Discounts to Pursue',
            value: 'discounts'
        },
        {
            label: 'Generate a Plan',
            value: 'generate-a-plan'
        }
    ]

    const handleSectionMenuItemClick = (value) => {
        if (value === currentView) {
            return;
        }
        setCurrentView(value);
    }

    // Add effect to handle body scroll
    useEffect(() => {
        document.body.style.overflow = 'hidden';

        // Cleanup function to restore scrolling when component unmounts
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    const handleCreatePlanComplete = (newPlan) => {
        setUserPlans([...userPlans, newPlan]);
    }

    return (
        <ContentWrapper
            inline={inline}
            handleOutsideClick={handleOutsideClick}
            deviceType={deviceType}
        >

            <div className={`what-if-machine-plan-summary-container ${deviceType}`}>
                <Header
                    currentView={currentView}
                    handleSectionMenuItemClick={handleSectionMenuItemClick}
                />
                <div className={`what-if-machine-plan-summary-content-container ${deviceType}`}>
                    {/* <Navigation
                            currentView={currentView}
                            handleSectionMenuItemClick={handleSectionMenuItemClick}
                            menuItems={sectionMenuItems}
                        /> */}
                    <div className={`what-if-machine-plan-summary-subsection-frame ${deviceType}`}>

                        {/* {(projectsList.length > 0 && simulatorData) && ( */}
                        {!simulatorData && (
                            <Summary
                                projectsList={projectsList}
                                handleSectionMenuItemClick={handleSectionMenuItemClick}
                                simulatorData={simulatorData}
                                handleSelectedProjectsListUpdate={handleSelectedProjectsListUpdate}
                                propertyData={propertyData}
                                notSelectedProjectsList={notSelectedProjectsList}
                                handleUserAddingAProject={handleUserAddingAProject}
                                handleUserRemovingAProject={handleUserRemovingAProject}
                                viewType={'changeProjects'}
                            />
                        )}
                        {(simulatorData) && (
                            <>
                                {(currentView === 'summary' || currentView === 'changeProjects') && (
                                    <Summary
                                        projectsList={projectsList}
                                        handleSectionMenuItemClick={handleSectionMenuItemClick}
                                        simulatorData={simulatorData}
                                        handleSelectedProjectsListUpdate={handleSelectedProjectsListUpdate}
                                        propertyData={propertyData}
                                        notSelectedProjectsList={notSelectedProjectsList}
                                        handleUserAddingAProject={handleUserAddingAProject}
                                        handleUserRemovingAProject={handleUserRemovingAProject}
                                        viewType={currentView}
                                    />
                                )}
                                {/* {currentView === 'solving-for' && (
                                        <SolvingFor
                                            projectsList={projectsList}
                                            simulatorData={simulatorData}
                                            propertyData={propertyData}
                                        />
                                    )} */}
                                {currentView === 'analysis' && (
                                    <Analysis
                                        projectsList={projectsList}
                                        simulatorData={simulatorData}
                                        propertyData={propertyData}
                                    />
                                )}
                                {/* {currentView === 'costs-vs-benefits' && (
                                        <CostBenefit />
                                    )} */}
                                {/* {currentView === 'payoff' && (
                                        <Payoff
                                            projectsList={projectsList}
                                            simulatorData={simulatorData}
                                            propertyData={propertyData}
                                        />
                                    )} */}
                                {currentView === 'generate-a-plan' && (
                                    <CreatePlanFlow
                                        projects={projectsList}
                                        onComplete={handleCreatePlanFromLabComplete}
                                        userInsurance={null}
                                        property_data={propertyData}
                                        property_id={propertyData.address_id}
                                        userPlans={userPlans}
                                        setUserPlans={setUserPlans}
                                        createFromLab={true}
                                        labProjectsList={projectsList}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </ContentWrapper>
    );
};

export default WhatIfMachinePlanSummary;